import { Injectable } from '@angular/core';
import { IAlertRulesFeature, IDeviceFeature, IGroupFeature, IMemberFeature, RoleNameType } from 'app/models/role.constant';

type FeatureMap<T> = {
  [K in keyof T]: (role: RoleNameType) => boolean;
};

@Injectable({ providedIn: 'root' })
export class FeatureAccessService {
  private readonly roleHierarchy: Record<RoleNameType, number> = {
    'owner': 4,
    'admin': 3,
    'read-write': 2,
    'read-only': 1
  };

  private hasAccess(requiredRole: RoleNameType, userRole: RoleNameType): boolean {
    return this.roleHierarchy[userRole] >= this.roleHierarchy[requiredRole];
  }

  private isOwnerAccess(role: RoleNameType): boolean {
    return this.hasAccess('owner', role);
  }

  private isAdminAccess(role: RoleNameType): boolean {
    return this.hasAccess('admin', role);
  }

  private isAllowModifyAccess(role: RoleNameType): boolean {
    return this.hasAccess('read-write', role);
  }

  private isAllowViewAccess(role: RoleNameType): boolean {
    return this.hasAccess('read-only', role);
  }

  private readonly groupFeatures: FeatureMap<IGroupFeature> = {
    'delete-group': this.isOwnerAccess.bind(this),
    'edit-group': this.isAdminAccess.bind(this),
    'access-page': this.isAllowViewAccess.bind(this)
  };

  private readonly deviceFeatures: FeatureMap<IDeviceFeature> = {
    'delete-device': this.isAllowModifyAccess.bind(this),
    'edit-device': this.isAllowModifyAccess.bind(this),
    'access-page': this.isAllowViewAccess.bind(this),
    'add-device': this.isAllowModifyAccess.bind(this),
    'control-breaker': this.isAllowModifyAccess.bind(this),
    'write-metadata': this.isAllowModifyAccess.bind(this),
    'crud-schedule': this.isAllowModifyAccess.bind(this)
  };

  private readonly alertRulesFeatures: FeatureMap<IAlertRulesFeature> = {
    'edit-alert-rules': this.isAllowModifyAccess.bind(this),
    'delete-alert-rules': this.isAllowModifyAccess.bind(this),
    'access-page': this.isAllowViewAccess.bind(this),
    'add-alert-rules': this.isAllowModifyAccess.bind(this)
  };

  private readonly memberFeatures: FeatureMap<IMemberFeature> = {
    'edit-role': this.isAdminAccess.bind(this),
    'invite-members': this.isOwnerAccess.bind(this),
    'kick-member': this.isAdminAccess.bind(this),
    'access-page': this.isAdminAccess.bind(this),
    'create-user': this.isOwnerAccess.bind(this)
  };

  /**
   * Check access for Group features
   * @param feature The group feature to check
   * @param role The user's role
   * @returns boolean indicating if the user has access
   */
  isAllowAccessGroupFeatures(feature: keyof IGroupFeature, role: RoleNameType): boolean {
    return this.groupFeatures[feature]?.(role) ?? false;
  }

  /**
   * Check access for Device features
   * @param feature The device feature to check
   * @param role The user's role
   * @returns boolean indicating if the user has access
   */
  isAllowAccessDeviceFeatures(feature: keyof IDeviceFeature, role: RoleNameType): boolean {
    return this.deviceFeatures[feature]?.(role) ?? false;
  }

  /**
   * Check access for Alert Rules features
   * @param feature The alert rules feature to check
   * @param role The user's role
   * @returns boolean indicating if the user has access
   */
  isAllowAccessAlertRulesFeature(feature: keyof IAlertRulesFeature, role: RoleNameType): boolean {
    return this.alertRulesFeatures[feature]?.(role) ?? false;
  }

  /**
   * Check access for Member features
   * @param feature The member feature to check
   * @param role The user's role
   * @returns boolean indicating if the user has access
   */
  isAllowAccessMemberFeature(feature: keyof IMemberFeature, role: RoleNameType): boolean {
    return this.memberFeatures[feature]?.(role) ?? false;
  }
}